// THE CLIENT'S OWN COPY ON THE DONE SCREEN, FOR A SHOP IN HIPAA MODE (2026-10-08; TW-0280, the form's slice; docs/HIPAA.md →
// What changes for a shop on the plan → What it loses: "The client's copy, where a state's rule hands the client one, is
// shown on the client's own screen at signing and printed by the shop.").
//
// Where a state's rule hands the client a copy of what they signed (the `client_copy` duty: Alabama, Illinois, Michigan and
// North Dakota today), every other shop's client gets it by EMAIL: the API sends the signed waiver, attached, to the address
// the client typed, the moment it lands (services/waiver-api/consolidated_api/client_copy.py). A shop in HIPAA mode mails its
// clients nothing (lane.UNAVAILABLE: `client_copy`, `thank_you`), so its client is handed the copy HERE, on the screen they
// signed on, and the shop's own email tells the shop to print them one as well (received_dynamodb_item.NOTICE_PRINT_COPY).
// Only that shop: every other shop's done screen is the one it has always been, byte for byte (SuccessScreen.test.tsx).
//
// BUILT FROM THE BODY THE FORM JUST POSTED, AND NOTHING ELSE. No read of storage, no link, no file, no address anybody else
// could open: the posted body says which boxes were ticked, which of the state's words were on the page and how each health
// question was answered, and the page hands over its own words for each (Form.tsx, at the submit), so the copy says what the
// client read and chose, in the language they signed in. It lives in this page's memory and is gone with "Submit another
// waiver" or a reload.
//
// WHAT IT DRAWS, AND WHY (a companion's call, Grace, 2026-10-08, on the brief's list):
//   * the shop's name, the form's title (which names the service: "Tattoo Consent and Release Form"), the client's name as
//     typed and the signed date: whose record, of what, and when;
//   * every box they ticked, in the page's order and its words: the service's clauses, the state's sentences they ticked,
//     the shop's own clauses, the card-fee box, the receipt of the practice's notice of privacy practices, the terms, either
//     optional box they ticked, and a parent's tick on the state's own statement. A box left alone is not something they
//     agreed to, and is not drawn;
//   * the state's own words the page GAVE them in that section (Michigan's disclosure and its aftercare suggestions): the
//     posted body names each one shown (`stateNotices`), the signed record prints each, and a copy of "the record" without
//     them, in the state whose rule says "a copy of the record", would not be one;
//   * the photo release, whole, when they signed it, because the release promises the client "a copy of this release"; and
//     one line saying they did not sign it when the shop offered it and they left it alone;
//   * every health question the page asked, in its words, with the answer as the signed record prints it ("Yes" with what
//     they typed, "No", and the one question a client may leave blank as not answered), and the written health boxes the
//     page showed. The answers are most of what the rule's "record" is, and they are the client's own, on their own screen.
//     And since TW-0412 (2026-10-20) the shop's own questions after them, where the page draws them, in the shop's words,
//     each with the line the page showed under the answer given, which the signed record prints too.
//   NOT DRAWN: the signature and the photo of the ID (pictures of a person's hand and card, on a page made to be printed on
//   whatever printer is nearest), the contact details, the artist, the price and the payment: none is something the client
//   agreed to, and the copy the shop prints carries every one of them.

import { DateTime } from "luxon";

import { dayOf } from "./day";
import type { Lang } from "./i18n";
import { dutyNamedBy, type NoticeSection } from "./stateForms";

/** The vocabulary's id for the duty (scripts/state-forms.py VOCAB; the API's client_copy.DUTY_ID). */
export const CLIENT_COPY_DUTY = "client_copy";

type ShopLike = { hipaa?: boolean; state?: string | null } | null | undefined;

/** Does this shop's done screen hand the client their copy? Only a shop in HIPAA mode (`/shop` says `hipaa: true`) whose
 *  state's rule names the `client_copy` duty on `day`, read off the contract the API reads, the API's way (stateForms.ts
 *  dutyNamedBy). A HIPAA shop in a state with no such rule keeps today's done screen; every other shop's client gets the copy
 *  by email, from the API, where the rule asks for one. */
export function showsClientCopy(shop: ShopLike, day: string = dayOf()): boolean {
  return shop?.hipaa === true && dutyNamedBy(shop?.state, CLIENT_COPY_DUTY, day);
}

/** The photo release as the page drew it (hipaaForm.ts releaseWords). */
export type ReleaseWords = { heading: string; lines: string[]; box: string };

/** What the page drew, in its own words and its own order, handed over by Form.tsx at the submit. Which of it the client
 *  ticked, was given or answered is the POSTED body's to say, never this. */
export type CopyPage = {
  lang: Lang;
  shop: string;
  /** the form's own title for the service signed (service.ts copyFor) */
  title: string;
  /** the service's clauses the page showed, in order (Form.tsx acksShown), each in its words */
  clauses: { key: string; text: string }[];
  /** the state's sentences the page showed, in order: one the client ticks, words they are given, or a sheet */
  notices: { id: string; acknowledge: boolean; text: string; sheet?: { intro: string; sections: NoticeSection[] } | null }[];
  ownClauses: { id: string; text: string }[];
  /** TW-0412 — the shop's own questions the page drew, in its words, each with its line and the answer the line shows under */
  ownQuestions?: { id: string; text: string; when: "yes" | "no" | null; note: string }[];
  cashPreferred: { id: string; text: string } | null;
  /** the box under the practice's notice of privacy practices, when the page drew it */
  privacyNoticeAck: string | null;
  /** the photo release, when the shop has it on */
  photoRelease: ReleaseWords | null;
  /** "I accept the terms and conditions." */
  terms: string;
  /** the two optional boxes, by the key each posts under */
  optional: { key: string; text: string }[];
  /** the state's own statement a parent ticks, in the rule's words (English whatever the language: minors.ts Attestation) */
  certification: string | null;
  /** TW-0526, the client-form side (2026-10-29) — every statement after the first the rule has the parent tick (Illinois's piercing
   *  rule three), by its id, in the words and the language the page showed it (`guardianStatements` in the body) */
  statements?: { id: string; text: string; lang: Lang }[];
  /** the health questions the page asked, in its order and words, each with the box a yes opens */
  health: { key: string; question: string; details?: string }[];
  /** the written health boxes the page showed (the medical history, the medications), by the key each posts under */
  notes: { key: string; label: string }[];
};

/** One line of what they signed: a box they ticked, words the page gave them, or a sheet (a line over sections). */
export type CopyLine =
  | { kind: "agreed"; text: string; lang?: Lang }
  | { kind: "given"; text: string }
  | { kind: "sheet"; text: string; intro: string; sections: NoticeSection[] };

/** A health question as the copy says it: its words, the answer the record carries, and what a yes said; and for one of the
 *  shop's own questions (TW-0412), the shop's line the page showed under that answer, only where it showed one. */
export type HealthAnswer = { question: string; answer: "yes" | "no" | ""; details: string; note?: string };

export type ClientCopy = {
  lang: Lang;
  shop: string;
  title: string;
  name: string;
  /** the signed date as posted, a calendar day (YYYY-MM-DD) */
  date: string;
  signed: CopyLine[];
  release: ReleaseWords | null;
  releaseDeclined: boolean;
  health: HealthAnswer[];
  notes: { label: string; text: string }[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);
const words = (value: unknown): string => (typeof value === "string" ? value.trim() : "");
/** The posted map says yes to this key: literally true, the way every box posts. */
const ticked = (map: unknown, key: string): boolean => isRecord(map) && map[key] === true;
const has = (map: Record<string, unknown>, key: string): boolean => Object.prototype.hasOwnProperty.call(map, key);

/** The client's copy: the page's words for exactly what the posted body says was ticked, given and answered. */
export function clientCopyOf(page: CopyPage, posted: Record<string, unknown>): ClientCopy {
  const acks = posted.acknowledgmentAndWaiver;
  const signed: CopyLine[] = [];
  for (const clause of page.clauses) if (ticked(acks, clause.key)) signed.push({ kind: "agreed", text: clause.text });
  for (const notice of page.notices) {
    if (!ticked(posted.stateNotices, notice.id)) continue;
    if (notice.acknowledge) signed.push({ kind: "agreed", text: notice.text });
    else if (notice.sheet) signed.push({ kind: "sheet", text: notice.text, intro: notice.sheet.intro, sections: notice.sheet.sections });
    else signed.push({ kind: "given", text: notice.text });
  }
  for (const clause of page.ownClauses) if (ticked(posted.ownClauses, clause.id)) signed.push({ kind: "agreed", text: clause.text });
  if (page.cashPreferred && ticked(posted.cashPreferred, page.cashPreferred.id)) {
    signed.push({ kind: "agreed", text: page.cashPreferred.text });
  }
  if (page.privacyNoticeAck && ticked(posted.privacyNotice, "received")) signed.push({ kind: "agreed", text: page.privacyNoticeAck });
  if (ticked(acks, "termsAndConditions")) signed.push({ kind: "agreed", text: page.terms });
  for (const box of page.optional) if (ticked(acks, box.key)) signed.push({ kind: "agreed", text: box.text });
  if (page.certification && ticked(acks, "guardianCertification")) {
    signed.push({ kind: "agreed", text: page.certification, lang: "en" });
  }
  for (const statement of page.statements ?? []) {
    if (ticked(posted.guardianStatements, statement.id)) signed.push({ kind: "agreed", text: statement.text, lang: statement.lang });
  }

  const release = isRecord(posted.photoRelease) ? posted.photoRelease : null;
  const questionnaire = isRecord(posted.questionnaire) ? posted.questionnaire : {};
  // a question is on the copy when the posted questionnaire carries it: the questionnaire is the record of what was asked
  const health: HealthAnswer[] = page.health
    .filter((question) => has(questionnaire, question.key))
    .map((question) => {
      const value = questionnaire[question.key];
      const answer = value === "yes" ? "yes" : value === "no" ? "no" : "";
      return { question: question.question, answer, details: answer === "yes" && question.details ? words(posted[question.details]) : "" };
    });
  // TW-0412 — and the shop's own questions the posted body answers, after the health questions where the page draws them, each
  // with the line the page showed under the answer given: the posted map is the record of what was asked, like the questionnaire
  const ownAnswers = isRecord(posted.ownQuestions) ? posted.ownQuestions : {};
  for (const question of page.ownQuestions ?? []) {
    if (!has(ownAnswers, question.id)) continue;
    const value = ownAnswers[question.id];
    const answer = value === "yes" ? "yes" : value === "no" ? "no" : "";
    const note = answer && question.note && question.when === answer ? question.note : "";
    health.push({ question: question.text, answer, details: "", ...(note ? { note } : {}) });
  }
  const notes = page.notes.filter((note) => has(posted, note.key)).map((note) => ({ label: note.label, text: words(posted[note.key]) }));

  return {
    lang: page.lang,
    shop: page.shop,
    title: page.title,
    name: [words(posted.firstName), words(posted.lastName)].filter(Boolean).join(" "),
    date: words(posted.signedDate),
    signed,
    release: page.photoRelease && release?.signed === true ? page.photoRelease : null,
    releaseDeclined: page.photoRelease !== null && release !== null && release.signed !== true,
    health,
    notes,
  };
}

/** The signed date as the client reads it, in the copy's language: "October 8, 2026", "8 de octubre de 2026". The date box
 *  holds a calendar day, read as one (in UTC, so no zone can move it a day); anything else is shown as it was posted. */
export function signedDayWords(day: string, lang: Lang): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return day;
  const at = DateTime.fromISO(day, { zone: "utc" });
  return at.isValid ? at.setLocale(lang === "es" ? "es" : "en-US").toLocaleString(DateTime.DATE_FULL) : day;
}
